import './chat.web.css';

import { useEffect, useMemo, useState } from 'react';
import { View } from 'react-native';

import { ChatMarkdown } from '@/components/chat/ChatMarkdown.web';
import { ChatToolActivity } from '@/components/chat/ChatToolActivity.web';
import {
  buildConversationLinkTargets,
  collectConversationBlockIds,
} from '@/lib/chat/context-links';
import { buildChatSegments } from '@/lib/chat/message-segments';
import type { ContextSet, LiveToolActivity, Message } from '@/lib/chat/types';
import { resolveBlockTitles } from '@/lib/databases/relation-titles';

type ChatMessageListProps = {
  messages: Message[];
  contextSet: ContextSet;
  streamingText: string | null;
  liveActivities: LiveToolActivity[];
  liveActivityCollapsed: boolean;
  onToggleLiveActivity: () => void;
};

export function ChatMessageList({
  messages,
  contextSet,
  streamingText,
  liveActivities,
  liveActivityCollapsed,
  onToggleLiveActivity,
}: ChatMessageListProps) {
  const [collapsedToolGroups, setCollapsedToolGroups] = useState<Record<string, boolean>>({});
  const [resolvedTitles, setResolvedTitles] = useState<Map<string, string>>(new Map());

  const segments = useMemo(() => buildChatSegments(messages), [messages]);
  const linkTargets = useMemo(
    () => buildConversationLinkTargets(contextSet, messages, resolvedTitles),
    [contextSet, messages, resolvedTitles],
  );

  useEffect(() => {
    const blockIds = collectConversationBlockIds(contextSet, messages);
    if (blockIds.length === 0) {
      setResolvedTitles(new Map());
      return;
    }
    void resolveBlockTitles(blockIds).then((resolved) => {
      const next = new Map<string, string>();
      for (const id of blockIds) {
        const title = resolved.get(id)?.title?.trim();
        next.set(id, title && title.length > 0 ? title : 'Untitled');
      }
      setResolvedTitles(next);
    });
  }, [contextSet, messages]);

  return (
    <View className="chat-message-scroll flex w-full flex-col gap-4 px-page py-page-y">
      {segments.map((segment) => {
        if (segment.kind === 'user') {
          const text = segment.message.content.text ?? '';
          if (!text.trim()) {
            return null;
          }

          return (
            <View key={segment.message.id} className="chat-message-segment chat-message chat-message-user">
              <ChatMarkdown content={text} linkTargets={linkTargets} />
            </View>
          );
        }

        if (segment.kind === 'assistant') {
          const text = segment.message.content.text ?? '';
          if (!text.trim()) {
            return null;
          }

          return (
            <View
              key={segment.message.id}
              className="chat-message-segment chat-message chat-message-assistant"
            >
              <ChatMarkdown content={text} linkTargets={linkTargets} />
            </View>
          );
        }

        const collapsed = collapsedToolGroups[segment.id] ?? true;
        const toolItems = segment.messages.map((message) => ({
          kind: 'history' as const,
          message,
        }));

        return (
          <View key={segment.id} className="chat-message-segment chat-tool-segment">
            <ChatToolActivity
              items={toolItems}
              collapsed={collapsed}
              onToggleCollapsed={() =>
                setCollapsedToolGroups((prev) => ({
                  ...prev,
                  [segment.id]: !collapsed,
                }))
              }
            />
          </View>
        );
      })}

      {streamingText != null || liveActivities.length > 0 ? (
        <View className="chat-message-segment chat-stream-segment flex w-full flex-col gap-3">
          {liveActivities.length > 0 ? (
            <ChatToolActivity
              items={liveActivities.map((activity) => ({ kind: 'live' as const, activity }))}
              collapsed={liveActivityCollapsed}
              onToggleCollapsed={onToggleLiveActivity}
            />
          ) : null}
          {streamingText != null ? (
            <View className="chat-message chat-message-assistant w-full">
              <ChatMarkdown content={streamingText} linkTargets={linkTargets} />
            </View>
          ) : null}
        </View>
      ) : null}
    </View>
  );
}
